import React, {
	createContext,
	useContext,
	useImperativeHandle,
	useMemo,
} from 'react';
import {useAsset, type UseAssetParams} from '@netx/core-asset-next/hooks';
import {
	type IContextChildren,
	type IContextRef,
	renderChildrenOrChildrenFunction,
} from '@netx/core-next';

export type AssetContextType = ReturnType<typeof useAsset>;

export const AssetContext = createContext<AssetContextType | undefined>(
	undefined,
);

export const useAssetProvider = () => {
	const context = useContext(AssetContext);
	if (context === undefined) {
		throw new Error('`useAssetProvider` must be used within a `AssetProvider`');
	}
	return context;
};

export type AssetProviderRef = IContextRef<AssetContextType>;

export type AssetProviderProps = UseAssetParams & {
	ref?: AssetProviderRef;
	children: IContextChildren<AssetContextType>;
};

const AssetProviderInner = (
	{children, ...props}: AssetProviderProps,
	ref: AssetProviderRef,
) => {
	const asset = useAsset(props);

	const contextValues = useMemo(() => asset, [asset]);

	useImperativeHandle(ref, () => contextValues, [contextValues]);

	return (
		<AssetContext.Provider value={contextValues}>
			{renderChildrenOrChildrenFunction(children, contextValues)}
		</AssetContext.Provider>
	);
};

export const AssetProviderPrimitive = React.forwardRef<
	AssetContextType,
	AssetProviderProps
>(AssetProviderInner);

/**
 * Asset context provider
 * @example
 * <AssetProvider ref={assetRef}>
 * 	...
 * </AssetProvider>
 * @example
 * <AssetProvider ref={assetRef}>
 * 	{(asset) => (
 * 		...
 * 	)}
 * </AssetProvider>
 */
export const AssetProvider = React.forwardRef(
	(props: AssetProviderProps, ref: AssetProviderRef) => {
		return <AssetProviderPrimitive ref={ref} {...props} />;
	},
);

export default AssetProvider;
